import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  FetchTransactionListPeriodReportRequest,
  TransactionListPeriodReportData,
} from "@/api/fetchQuickBooksTransactionListPeriodReport/types";
import { AppDispatch, AppThunk } from "@/app/store";
import { fetchQuickBooksTransactionListPeriodReport } from "@/api/fetchQuickBooksTransactionListPeriodReport/fetchQuickBooksTransactionListPeriodReportApi";

interface TransactionListReportState {
  report: TransactionListPeriodReportData | null;
  loading: boolean;
  error: string | null;
}

const initialState: TransactionListReportState = {
  report: null,
  loading: false,
  error: null,
};

const transactionListReportSlice = createSlice({
  name: "transactionListReport",
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setReport(
      state,
      action: PayloadAction<TransactionListPeriodReportData | null>,
    ) {
      state.report = action.payload;
    },
    clearReport(state) {
      state.report = null;
      state.error = null;
    },
  },
});

export const { setLoading, setError, setReport, clearReport } =
  transactionListReportSlice.actions;

export const fetchTransactionListPeriodReportAction =
  (requestData: FetchTransactionListPeriodReportRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    dispatch(setReport(null)); // Clear old report before fetching new one
    try {
      const response =
        await fetchQuickBooksTransactionListPeriodReport(requestData);
      // Access response.data to get the report data for the Redux store
      if (response && response.data) {
        dispatch(setReport(response.data));
      } else {
        dispatch(setError("Invalid response format"));
      }
    } catch (error) {
      // Report is already cleared at the start, so error state is clean
      if (error instanceof Error) {
        dispatch(setError(error.message));
      } else {
        dispatch(setError("Failed to fetch transaction list report"));
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

export default transactionListReportSlice.reducer;
